import { useRef, useState } from "react";
import clsx from "clsx";
import { Button } from "../ui/Button";
import { Toggle } from "../ui/Toggle";
import type { FieldSpec, FieldType, InterviewTemplate } from "../../templates/types";

const FIELD_TYPES: FieldType[] = ["integer", "enum", "boolean_with_notes", "string_array", "verbatim"];

const FIELD_TYPE_LABEL: Record<FieldType, string> = {
  integer: "Number",
  enum: "Multiple choice",
  boolean_with_notes: "Yes/No (with notes)",
  string_array: "List of short answers",
  verbatim: "Verbatim notes (not asked)",
};

// Mirrors the contract the server now applies to EVERY template at
// /api/interview/start (Fieldnote::Contracts::DesignProposal plus the
// snake_case id rule in CheckInRequest). The server is the authority; this
// copy only exists so an edit that would be rejected is caught here, with a
// reason, instead of the check-in silently failing to start.
const FIELD_ID_FORMAT = /^[a-z][a-z0-9_]*$/;
const MAX_FIELDS = 12;
const MAX_INSTRUCTIONS = 2000;
const MAX_LABEL = 200;
// Field ids, labels, options and prompt hints are each written into one line
// of the model's instructions, so the server refuses a line break or control
// character in any of them (CheckInRequest#single_line_strings?). Only the
// interview instructions may span lines.
const NOT_SINGLE_LINE = /[\u0000-\u001F\u007F-\u009F\u2028\u2029]/;

export function templateProblems(template: InterviewTemplate): string[] {
  const problems: string[] = [];
  const fields = template.fields ?? [];
  if (fields.length === 0) problems.push("Add at least one field.");
  if (fields.length > MAX_FIELDS) problems.push(`Use at most ${MAX_FIELDS} fields.`);
  const instructions = (template.instructions ?? "").trim();
  if (!instructions) problems.push("Interview instructions cannot be empty.");
  if ((template.instructions ?? "").length > MAX_INSTRUCTIONS) {
    problems.push(`Interview instructions must be at most ${MAX_INSTRUCTIONS} characters.`);
  }
  const ids = fields.map((f) => f.id);
  if (ids.some((id) => !FIELD_ID_FORMAT.test(id ?? ""))) problems.push("Every field id must be lowercase snake_case.");
  if (new Set(ids).size !== ids.length) problems.push("Field ids must be unique.");
  fields.forEach((field, index) => {
    const name = field.label?.en?.trim() || `Field ${index + 1}`;
    if (!field.label?.en?.trim() || !field.label?.es?.trim()) problems.push(`${name} needs an English and a Spanish label.`);
    if ((field.label?.en ?? "").length > MAX_LABEL || (field.label?.es ?? "").length > MAX_LABEL) {
      problems.push(`${name}'s label must be at most ${MAX_LABEL} characters.`);
    }
    for (const bound of [field.min, field.max]) {
      if (bound !== undefined && bound !== null && !Number.isInteger(bound)) problems.push(`${name}'s minimum and maximum must be whole numbers.`);
    }
    const oneLine = [
      field.id,
      field.label?.en,
      field.label?.es,
      ...(field.options ?? []),
      ...(field.promptHints?.en ?? []),
      ...(field.promptHints?.es ?? []),
      ...Object.entries(field.synonyms ?? {}).flat(2),
    ];
    if (oneLine.some((text) => typeof text === "string" && NOT_SINGLE_LINE.test(text))) {
      problems.push(`${name}'s label, options and hints must each fit on one line.`);
    }
  });
  if (NOT_SINGLE_LINE.test(template.id ?? "")) problems.push("The template id must fit on one line.");
  return [...new Set(problems)];
}

function moveItem<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

function download(filename: string, content: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * §12's MANDATORY review step. This is where the product's thesis — "the
 * LLM proposes, the application decides" — is most legible: every field
 * here was proposed by a provider (or a fixed Demo fixture), and NONE of it
 * is runnable until a human has looked at this screen and clicked "Run this
 * interview." That is also this screen's safety function: it is the last
 * chance to catch a malformed or hallucinated schema before it reaches a
 * live interview. There is deliberately no path from SchemaComposer to a
 * running check-in that skips this component.
 *
 * No digest is computed here, and this component never calls
 * /api/interview/start itself (§7) — it only ever hands the locally edited
 * template up via onApprove; the digest is computed server-side, once,
 * after this approval, when the SAME engine that runs the default template
 * bootstraps this one.
 */
export function SchemaReviewForm({
  template: initialTemplate,
  notice,
  onApprove,
}: {
  template: InterviewTemplate;
  notice: string;
  onApprove: (template: InterviewTemplate) => void;
}) {
  const [template, setTemplate] = useState<InterviewTemplate>(initialTemplate);
  const problems = templateProblems(template);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  function updateField(index: number, patch: Partial<FieldSpec>) {
    setTemplate((t) => ({
      ...t,
      fields: t.fields.map((f, i) => (i === index ? { ...f, ...patch } : f)),
    }));
  }

  function removeField(index: number) {
    setTemplate((t) => ({ ...t, fields: t.fields.filter((_, i) => i !== index) }));
  }

  function reorder(index: number, direction: -1 | 1) {
    setTemplate((t) => ({ ...t, fields: moveItem(t.fields, index, index + direction) }));
  }

  function updateOption(fieldIndex: number, optionIndex: number, value: string) {
    const field = template.fields[fieldIndex];
    const options = [...(field.options ?? [])];
    options[optionIndex] = value;
    updateField(fieldIndex, { options });
  }

  function addOption(fieldIndex: number) {
    const field = template.fields[fieldIndex];
    updateField(fieldIndex, { options: [...(field.options ?? []), ""] });
  }

  function removeOption(fieldIndex: number, optionIndex: number) {
    const field = template.fields[fieldIndex];
    updateField(fieldIndex, { options: (field.options ?? []).filter((_, i) => i !== optionIndex) });
  }

  function downloadTemplate() {
    download(`${template.id || "fieldnote_template"}.json`, JSON.stringify(template, null, 2), "application/json;charset=utf-8");
  }

  function loadTemplateFile(file: File) {
    setLoadError(null);
    file
      .text()
      .then((text) => {
        const parsed = JSON.parse(text);
        if (!parsed || typeof parsed !== "object" || !Array.isArray(parsed.fields)) {
          throw new Error("not a template");
        }
        setTemplate(parsed as InterviewTemplate);
      })
      .catch(() => setLoadError("That file doesn't look like a Fieldnote template — expected id/instructions/fields."));
  }

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto p-6">
      {/* text-ink rather than text-clay on bg-clay-dim: that pair measures
          ~3.48:1, short of the 4.5:1 AA text threshold (see Pill.tsx). The
          tint + border still carry the notice's tone; only the text color
          changes. */}
      <div className="rounded-lg border border-clay/40 bg-clay-dim p-3 text-sm text-ink">{notice}</div>

      <div>
        <h1 className="font-display text-xl text-ink">Review before running</h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-soft">
          Nothing runs until you approve it here. Edit labels, types, options, and order as needed, then
          run the interview — it uses the exact same engine as the default check-in.
        </p>
      </div>

      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium text-ink">Interview instructions</span>
        <textarea
          value={template.instructions}
          onChange={(e) => setTemplate((t) => ({ ...t, instructions: e.target.value }))}
          rows={3}
          className="w-full resize-none rounded-lg border border-border bg-paper p-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-sage-strong"
        />
      </label>

      <ul className="flex flex-col gap-3">
        {template.fields.map((field, index) => (
          <li key={index} className="rounded-lg border border-border bg-paper p-3">
            <div className="flex flex-wrap items-center gap-2">
              <label className="flex-1 min-w-[10rem] text-sm">
                <span className="sr-only">Label for field {index + 1}</span>
                <input
                  value={field.label.en}
                  onChange={(e) => updateField(index, { label: { ...field.label, en: e.target.value } })}
                  className="w-full rounded-lg border border-border bg-paper p-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-sage-strong"
                  aria-label={`Label for field ${index + 1}`}
                />
              </label>

              <select
                value={field.type}
                onChange={(e) => updateField(index, { type: e.target.value as FieldType })}
                aria-label={`Type for ${field.label.en || `field ${index + 1}`}`}
                className="rounded-lg border border-border bg-paper p-2 text-sm text-ink"
              >
                {FIELD_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {FIELD_TYPE_LABEL[type]}
                  </option>
                ))}
              </select>

              <Toggle checked={field.required} onChange={(v) => updateField(index, { required: v })} label={`Required — ${field.label.en}`} />
              <span className="text-xs text-ink-soft">Required</span>

              <Toggle checked={field.askable} onChange={(v) => updateField(index, { askable: v })} label={`Askable — ${field.label.en}`} />
              <span className="text-xs text-ink-soft">Askable</span>

              <div className="flex gap-1">
                <Button type="button" variant="ghost" onClick={() => reorder(index, -1)} disabled={index === 0} aria-label={`Move ${field.label.en || "field"} up`}>
                  ↑
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => reorder(index, 1)}
                  disabled={index === template.fields.length - 1}
                  aria-label={`Move ${field.label.en || "field"} down`}
                >
                  ↓
                </Button>
                <Button type="button" variant="danger" onClick={() => removeField(index)} aria-label={`Delete ${field.label.en || "field"}`}>
                  Delete
                </Button>
              </div>
            </div>

            {field.type === "enum" && (
              <div className="mt-3 flex flex-col gap-1.5 border-t border-border pt-3">
                <span className="text-xs font-medium text-ink-soft">Options</span>
                {(field.options ?? []).map((option, optionIndex) => (
                  <div key={optionIndex} className="flex items-center gap-2">
                    <input
                      value={option}
                      onChange={(e) => updateOption(index, optionIndex, e.target.value)}
                      aria-label={`Option ${optionIndex + 1} for ${field.label.en}`}
                      className="w-full max-w-xs rounded-lg border border-border bg-paper p-1.5 text-sm text-ink"
                    />
                    <Button type="button" variant="ghost" onClick={() => removeOption(index, optionIndex)} aria-label={`Remove option ${optionIndex + 1}`}>
                      Remove
                    </Button>
                  </div>
                ))}
                <div>
                  <Button type="button" variant="secondary" onClick={() => addOption(index)}>
                    Add option
                  </Button>
                </div>
              </div>
            )}

            {field.type === "integer" && (
              <div className="mt-3 flex items-center gap-3 border-t border-border pt-3 text-sm">
                <label className="flex items-center gap-1.5">
                  <span className="text-ink-soft">Min</span>
                  <input
                    type="number"
                    value={field.min ?? ""}
                    onChange={(e) => updateField(index, { min: e.target.value === "" ? undefined : Number(e.target.value) })}
                    aria-label={`Minimum for ${field.label.en}`}
                    className="w-20 rounded-lg border border-border bg-paper p-1.5 text-ink"
                  />
                </label>
                <label className="flex items-center gap-1.5">
                  <span className="text-ink-soft">Max</span>
                  <input
                    type="number"
                    value={field.max ?? ""}
                    onChange={(e) => updateField(index, { max: e.target.value === "" ? undefined : Number(e.target.value) })}
                    aria-label={`Maximum for ${field.label.en}`}
                    className="w-20 rounded-lg border border-border bg-paper p-1.5 text-ink"
                  />
                </label>
              </div>
            )}
          </li>
        ))}
      </ul>

      {loadError && (
        <p role="alert" className="text-sm text-danger">
          {loadError}
        </p>
      )}

      {problems.length > 0 && (
        <div role="status" className="rounded-md border border-border bg-paper-dim p-3 text-sm text-ink">
          <p className="font-medium">Fix these before running the interview:</p>
          <ul className="mt-1 list-disc pl-5">
            {problems.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        </div>
      )}

      <div className={clsx("flex flex-wrap items-center gap-2 border-t border-border pt-4")}>
        <Button type="button" onClick={() => onApprove(template)} disabled={problems.length > 0}>
          Run this interview
        </Button>
        <Button type="button" variant="secondary" onClick={downloadTemplate}>
          Download template JSON
        </Button>
        <Button type="button" variant="secondary" onClick={() => fileInputRef.current?.click()}>
          Load template JSON
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/json"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) loadTemplateFile(file);
            e.target.value = "";
          }}
        />
      </div>
    </div>
  );
}
